/**
 * useQueryObject Hook
 *
 * Manages complex state in URL query parameters with automatic compression,
 * sessionStorage fallback, and memory-efficient caching.
 *
 * Features:
 * - LZ-String compression for URL efficiency
 * - Automatic sessionStorage fallback for large state (>2500 chars)
 * - Batches multiple updates to prevent URL thrashing
 * - Memory-leak free with bounded LRU cache
 * - Cross-tab synchronization via storage events
 * - SSR-safe with graceful degradation
 *
 * @example
 * ```tsx
 * const [filters, updateFilters] = useQueryObject('dashboard-filters', {
 *   dateRange: { start: '2024-01-01', end: '2024-12-31' },
 *   channels: [],
 *   status: 'active'
 * });
 *
 * // Update entire object
 * updateFilters({ channels: ['email', 'sms'] });
 *
 * // Update specific section
 * updateFilters('dateRange', { start: '2024-06-01' });
 *
 * // Control history behavior
 * updateFilters({ status: 'inactive' }, { replace: true });
 * ```
 */

import * as React from 'react';
import {useHistory} from 'src/rerouter';
import LZString from 'lz-string';
import logger from 'src/utils/logger';

// Native JS replacements for lodash (zero bundle cost)
const assign = Object.assign;
const isPlainObject = (val: any): boolean => val?.constructor === Object;
const keys = Object.keys;
const has = (obj: any, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(obj, key);

type Options = {replace?: boolean};

const MAX_SAFE_URL_LENGTH = 2500;
const OVERFLOW_PREFIX = '__local__:';

// Module-level cache for sessionStorage reads
const storageCache = new Map<string, string | null>();
const MAX_CACHE_SIZE = 50;

function getCachedSessionStorage(key: string): string | null {
  if (!storageCache.has(key)) {
    // LRU eviction: remove oldest entry if cache is full
    if (storageCache.size >= MAX_CACHE_SIZE) {
      const firstKey = storageCache.keys().next().value;
      if (firstKey) {
        storageCache.delete(firstKey);
      }
    }

    try {
      storageCache.set(key, sessionStorage.getItem(key));
    } catch {
      storageCache.set(key, null);
    }
  }

  return storageCache.get(key)!;
}

function setCachedSessionStorage(key: string, value: string): boolean {
  try {
    sessionStorage.setItem(key, value);
    storageCache.set(key, value);
    return true;
  } catch {
    return false;
  }
}

function cleanupOldSessionStorage(currentKey: string, queryKey: string): void {
  try {
    const prefix = OVERFLOW_PREFIX;
    const keysToRemove: string[] = [];

    const expectedPrefix = `${prefix}${queryKey}`;

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(expectedPrefix) && key !== currentKey) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      sessionStorage.removeItem(key);
      storageCache.delete(key);
    });
  } catch {
    // Ignore errors - storage might be unavailable
  }
}

/**
 * Hook for managing complex state in URL query parameters
 *
 * @param queryKey - Unique key for this state in URL query params
 * @param defaultValue - Default state (MUST be stable reference - use constant or useMemo)
 * @returns Tuple of [state, updateFunction]
 */
export function useQueryObject<T extends object = Record<string, any>>(
  queryKey: string,
  defaultValue: T,
): [
  T,
  {
    <K extends keyof T>(
      section: K,
      update: Partial<T[K]>,
      options?: Options,
    ): void;
    (partial: Partial<T>, options?: Options): void;
  },
] {
  const router = useHistory();

  const defaultValueRef = React.useRef(defaultValue);
  React.useEffect(() => {
    defaultValueRef.current = defaultValue;
  }, [defaultValue]);

  const raw = router.location.query?.[queryKey] as string | undefined;

  const parsedFromUrl: T = React.useMemo(() => {
    if (!raw) {
      return defaultValueRef.current;
    }

    // Overflow pointer case: state in sessionStorage
    if (raw.startsWith(OVERFLOW_PREFIX)) {
      try {
        const stored = getCachedSessionStorage(raw);
        return stored
          ? mergeDefaults(defaultValueRef.current, JSON.parse(stored))
          : defaultValueRef.current;
      } catch {
        return defaultValueRef.current;
      }
    }

    // Normal compressed state in URL
    try {
      const json = LZString.decompressFromEncodedURIComponent(raw);
      if (!json) {
        return defaultValueRef.current;
      }
      return mergeDefaults(defaultValueRef.current, JSON.parse(json));
    } catch {
      return defaultValueRef.current;
    }
  }, [raw]);

  const [state, setState] = React.useState<T>(parsedFromUrl);

  const latestRef = React.useRef<T>(parsedFromUrl);
  const pendingRef = React.useRef<Partial<T>>({});
  const optionsRef = React.useRef<Options | undefined>(undefined);
  const scheduledRef = React.useRef<boolean>(false);
  const isMountedRef = React.useRef<boolean>(true);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Cross-tab synchronization
  React.useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key?.startsWith(OVERFLOW_PREFIX)) {
        storageCache.delete(e.key);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        storageCache.clear();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Sync URL → state
  React.useEffect(() => {
    if (!shallowEqualObjects(state, parsedFromUrl)) {
      setState(parsedFromUrl);
      latestRef.current = parsedFromUrl;
    }
  }, [parsedFromUrl, state]);

  React.useEffect(() => {
    latestRef.current = state;
  }, [state]);

  const routerRef = React.useRef(router);
  React.useEffect(() => {
    routerRef.current = router;
  }, [router]);

  // Flush batched updates. When unmounted we still persist to URL so the next
  // mount (or other tabs) sees the update; we only skip setState to avoid
  // updating unmounted component state.
  const flush = React.useCallback(() => {
    logger.debug(`[useQueryObject:${queryKey}] FLUSH STARTED`);

    const unmounted = !isMountedRef.current;
    scheduledRef.current = false;

    const pending = pendingRef.current;
    pendingRef.current = {};

    if (!pending || keys(pending).length === 0) {
      if (!unmounted) {
        logger.debug(
          `[useQueryObject:${queryKey}] ABORTED: No pending changes`,
        );
      }
      return;
    }

    const prev = latestRef.current;
    const next = mergeSectionsShallow(prev, pending) as T;

    logger.debug(`[useQueryObject:${queryKey}] MERGE RESULT:`, {
      prev,
      pending,
      next,
      areEqual: shallowEqualObjects(prev, next),
    });

    if (shallowEqualObjects(prev, next)) {
      logger.debug(
        `[useQueryObject:${queryKey}] ABORTED: No changes detected (shallow equal)`,
      );
      return;
    }

    const json = JSON.stringify(next);
    const compressed = LZString.compressToEncodedURIComponent(json);

    const currentRouter = routerRef.current;
    const {location} = currentRouter;
    const newQuery = {...(location.query || {})};

    if (compressed.length <= MAX_SAFE_URL_LENGTH) {
      newQuery[queryKey] = compressed;
      cleanupOldSessionStorage('', queryKey);
    } else {
      const pointer = `${OVERFLOW_PREFIX}${queryKey}`;
      const success = setCachedSessionStorage(pointer, json);

      if (success) {
        newQuery[queryKey] = pointer;
        cleanupOldSessionStorage(pointer, queryKey);
      } else {
        if (isMountedRef.current) {
          setState(() => next);
          latestRef.current = next;
        }
        return;
      }
    }

    const opts = optionsRef.current || {};
    optionsRef.current = undefined;

    logger.debug(`[useQueryObject:${queryKey}] UPDATING URL:`, {
      queryKey,
      compressed: newQuery[queryKey],
      method: opts.replace ? 'replace' : 'push',
      fullQuery: newQuery,
    });

    if (opts.replace) {
      currentRouter.replace({...location, query: newQuery});
    } else {
      currentRouter.push({...location, query: newQuery});
    }

    logger.debug(`[useQueryObject:${queryKey}] URL UPDATED SUCCESSFULLY`);

    if (isMountedRef.current) {
      setState(() => next);
      latestRef.current = next;
    }
  }, [queryKey]);

  const scheduleFlush = React.useCallback(() => {
    if (scheduledRef.current) {
      return;
    }

    scheduledRef.current = true;
    Promise.resolve().then(flush);
  }, [flush]);

  const update = React.useCallback(
    (...args: any[]) => {
      let partial: Partial<T> = {};
      let opts: Options | undefined;

      if (typeof args[0] === 'string') {
        partial = {[args[0]]: args[1]} as Partial<T>;
        opts = args[2];
      } else {
        partial = args[0] ?? {};
        opts = args[1];
      }

      logger.debug(`[useQueryObject:${queryKey}] UPDATE CALLED:`, {
        partial,
        currentState: latestRef.current,
      });

      pendingRef.current = mergeSectionsShallow(pendingRef.current, partial);

      if (opts) {
        optionsRef.current = opts;
      }

      scheduleFlush();
    },
    [scheduleFlush, queryKey],
  );

  return [state, update as any];
}

/**
 * Decompresses an LZString-encoded query param and returns the raw string.
 * Use when you need the string (e.g. to parse with custom logic).
 *
 * @param query - URL query object (e.g. router.location.query)
 * @param key - Query param key (e.g. 'workflow-dashboard')
 * @returns Decompressed string or null if missing/invalid
 */
export function getDecompressedQueryParam(
  query: Record<string, string | undefined> | undefined,
  key: string,
): string | null {
  const raw = query?.[key] || '';
  if (!raw) {
    return null;
  }
  return LZString.decompressFromEncodedURIComponent(raw);
}

/**
 * Decompresses an LZString-encoded query param and parses it as JSON.
 * Returns defaultValue when the param is missing, empty, or invalid JSON.
 *
 * @param query - URL query object (e.g. router.location.query)
 * @param key - Query param key (e.g. 'workflow-dashboard')
 * @param defaultValue - Fallback when decompression/parse fails
 * @returns Parsed object of type T or defaultValue
 */
export function getDecompressedQueryObject<T extends object>(
  query: Record<string, string | undefined> | undefined,
  key: string,
  defaultValue: T,
): T {
  const decompressed = getDecompressedQueryParam(query, key);
  if (!decompressed) {
    return defaultValue;
  }
  try {
    return JSON.parse(decompressed) as T;
  } catch {
    return defaultValue;
  }
}

/**
 * Compresses an object for use in URL query params (JSON.stringify + LZString).
 * Use when writing a query param that will be read via getDecompressedQueryParam/Object.
 *
 * @param value - Object to compress (will be JSON.stringified then encoded)
 * @returns Encoded string for the query param, or empty string if compression fails
 */
export function compressJsonToLZString(value: object): string {
  return LZString.compressToEncodedURIComponent(JSON.stringify(value)) || '';
}

/**
 * Merges query state with default values
 * Strategy: shallow merge for nested objects, replace for arrays
 */
function mergeDefaults<T extends object>(
  defaults: T,
  fromQuery: Partial<T>,
): T {
  const out: T = {...defaults};

  for (const key of keys(fromQuery) as (keyof T)[]) {
    const dv = defaults[key];
    const qv = fromQuery[key];

    if (isPlainObject(dv) && isPlainObject(qv)) {
      (out as any)[key] = assign({}, dv as object, qv as object);
    } else if (qv !== undefined) {
      (out as any)[key] = qv;
    }
  }

  return out;
}

/**
 * Shallow merges two partial state objects for batching updates
 */
function mergeSectionsShallow<T extends object>(
  a: Partial<T>,
  b: Partial<T>,
): Partial<T> {
  const out: Partial<T> = {...a};

  for (const key of keys(b) as (keyof T)[]) {
    const av = a[key];
    const bv = b[key];

    if (isPlainObject(av) && isPlainObject(bv)) {
      (out as any)[key] = assign({}, av as object, bv as object);
    } else {
      (out as any)[key] = bv;
    }
  }

  return out;
}

/**
 * Shallow equality check for objects
 */
function shallowEqualObjects(a: any, b: any): boolean {
  if (a === b) {
    return true;
  }

  if (!a || !b) {
    return false;
  }

  const ak = keys(a);
  const bk = keys(b);

  if (ak.length !== bk.length) {
    return false;
  }

  for (const k of ak) {
    if (!has(b, k)) {
      return false;
    }
    if (a[k] !== b[k]) {
      return false;
    }
  }

  return true;
}
